import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Table from 'components/Table';
import ROLES from 'constants/roles';
import SectionHeader from 'components/SectionHeader';
import { useNavigate } from 'react-router-dom';
import { useCounter } from 'utils/customHooks';
import { PlusCircle, Search } from 'lucide-react';
import {
  ROUTE_ADMIN_PATH, ROUTE_MENTOR_PATH, ROUTE_PATH, ROUTE_POSITION_PATH, ROUTE_STARTUP_PATH,
} from 'routes';
import { fetchUserStartups } from '../modules/actions';
import StartupRow from './StartupRow';
import EmptyPreHome from './EmptyPrehome';
import './styles.scss';

function PreHome(props) {
  const {
    literals,
    literalsOnBoarding,
    user,
    platform,
  } = props;

  const [showSearch, setShowSearch] = useState(false);
  const [tabs, setTabs] = useState([{ key: 'all', name: literals.tabs.all }]);
  const [acceptedStartups, setAcceptedStartups] = useState([]);
  const [pendingStartups, setPendingStartups] = useState(false);
  const [loaded, setLoaded] = useState({ firstLoad: false, pending: false, accepted: false });
  const tableRefreshFlag = useCounter(0);
  const navigate = useNavigate();

  const fetchStartups = async (page, size, search, filters) => {
    const auxAcceptedStartups = await fetchUserStartups(page, size, search, { ...filters, status: 'accepted' }, '+name');
    if (!loaded.firstLoad) {
      setLoaded(prev => ({ ...prev, firstLoad: !!prev.pending, accepted: true }));
    }
    setAcceptedStartups(auxAcceptedStartups.items);
    return auxAcceptedStartups;
  };

  useEffect(() => {
    if (user?.role?.includes(ROLES.ROLE_USER)) {
      (async () => {
        const auxPendingStartups = await fetchUserStartups(0, 0, '', { status: 'pending', startedBy: 'startup' });
        if (auxPendingStartups.total > 0) {
          setTabs(prev => ([...prev, { key: 'pending', name: `${literals.tabs.pending} (${auxPendingStartups.total})` }]));
        }
        setPendingStartups(auxPendingStartups.items);
        setLoaded(prev => ({
          ...prev, firstLoad: !!prev.accepted, pending: { total: auxPendingStartups.total },
        }));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && user.id) {
      if (user.role.length === 1 && user.role[0] === ROLES.ROLE_INVESTOR) {
        navigate(ROUTE_POSITION_PATH.OPPORTUNITIES);
      } else if (user.role.includes(ROLES.ROLE_USER)) {
        if ((JSON.parse(localStorage.getItem('preferences')) || {})?.mode === 'investor') {
          navigate(ROUTE_PATH.INVESTOR_INVESTMENTS);
        }
        // if user is startup then redirect to onboarding if no startups, if at least 1 then let user select a startup already created or do another onboarding
        if (loaded.firstLoad && !loaded.pending.total) {
          if (acceptedStartups.length === 0) {
            navigate(ROUTE_STARTUP_PATH.ONBOARDING);
          } else if (acceptedStartups.length === 1) {
            if (user.role.length === 1 && !platform?.properties?.signup?.prehome) {
              navigate(ROUTE_STARTUP_PATH.setHome(acceptedStartups[0].id));
            }
          }
        }
      } else if (user.role.includes(ROLES.ROLE_MENTOR)) {
        // if !headline redirect to onboarding mentor because it is the first mandatory question
        if (user.mentor.headline) {
          navigate(ROUTE_MENTOR_PATH.HOME);
        } else {
          navigate(ROUTE_MENTOR_PATH.ONBOARDING);
        }
      } else if (user.role.includes(ROLES.ROLE_ADMIN)) {
        navigate(ROUTE_ADMIN_PATH.HOME);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loaded.firstLoad]);

  if (!user?.id || !user.role.includes(ROLES.ROLE_USER)) {
    return null;
  }

  const componentProps = {
    literals,
    literalsOnBoarding,
    user,
    setPendingStartups,
    refreshTable: tableRefreshFlag.increase,
  };

  if (loaded.accepted && !acceptedStartups.length) {
    return <EmptyPreHome />;
  }

  return (
    <SectionHeader
      tabs={tabs}
      actions={[
        {
          className: 'btn-icon',
          icon: Search,
          color: showSearch ? 'secondary' : 'tertiary',
          onClick: () => setShowSearch(prev => !prev),
        },
        // {
        //   text: literals.viewMyPosition,
        //   iconRight: Wallet,
        //   onClick: () => navigate(ROUTE_PATH.MY_POSITION),
        // },
        {
          text: literals.createInvestment,
          iconRight: PlusCircle,
          color: 'primary',
          onClick: () => navigate(ROUTE_PATH.ONBOARDING),
          hidden: !platform?.properties?.signup?.prehome,
        },
      ]}
    >
      {tab => (
        <div className='prehome_startups_content'>
          <Table
            searcher={showSearch}
            mode='components'
            component={StartupRow}
            componentColumns={{
              sm: 1, md: 2, lg: 2, xl: 3,
            }}
            componentProps={componentProps}
            pageSize={12}
            fetch={tab === 'pending'
              ? () => ({ items: pendingStartups, total: pendingStartups.length })
              : fetchStartups}
            forceFetch={tab}
          />
        </div>
      )}
    </SectionHeader>
  );
}

PreHome.propTypes = {
  literals: PropTypes.object.isRequired,
  literalsOnBoarding: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  platform: PropTypes.object.isRequired,
};
export default PreHome;
